import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { getErrorMessage } from '../../lib/utils';
import {
  ChevronRight, AlertCircle, Eye, Snowflake,
  AlertTriangle, Clock, Check, Play, Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IniciarDialog({
  open,
  onOpenChange,
  selectedOf,           // OF seleccionada
  producaoAtiva,        // Produção ativa atual (se houver)
  operacoesCongeladas = [],
  loading = false,
  setLoading,
  isSimulation = false,
  simUser = null,
  user,
  modoTestes = false,
  operacoes = [],       // Lista completa de operações
  consultarLoteCompleto,
  safeNavigate,
}) {
  const navigate = useNavigate();
  
  // Estado interno do dialog
  const [lote, setLote] = useState(null);
  const [operacaoId, setOperacaoId] = useState(null);
  const [qtdIniciar, setQtdIniciar] = useState(null);
  const [opsDisp, setOpsDisp] = useState([]);
  const [opsOriginais, setOpsOriginais] = useState([]);

  const handleClose = (v) => {
    if (!v) {
      setLote(null);
      setOperacaoId(null);
      setQtdIniciar(null);
      setOpsDisp([]);
      setOpsOriginais([]);
    }
    onOpenChange(v);
  };

  const handleStart = async () => {
    if (!selectedOf || !operacaoId) {
      toast.error('Selecione uma operação');
      return;
    }
    if (!lote) {
      toast.error('Selecione um lote');
      return;
    }

    // Verificar peças disponíveis
    const loteObj = selectedOf?.lotes?.find((l) => l.id === lote);
    const opInfo = loteObj?.operacoes_operador?.find((o) => o.operacao_id === operacaoId);
    if (loteObj && opInfo) {
      const qtdTotal = loteObj.quantidade_ajustada || loteObj.quantidade || 0;
      const qtdProduzidaTodos = opInfo?.quantidade_produzida_todos || 0;
      const totalAnomalias = loteObj.total_anomalias || 0;
      const qtdDisponivel = Math.max(0, qtdTotal - qtdProduzidaTodos - totalAnomalias);
      if (qtdDisponivel <= 0) {
        toast.error('Este lote não tem peças disponíveis para produção');
        return;
      }
    }

    setLoading && setLoading(true);
    try {
      let opsParaIniciar = [];
      if (operacaoId.startsWith('grupo_')) {
        const grupo = operacaoId.replace('grupo_', '');
        opsParaIniciar = (selectedOf?.operacoes || [])
          .filter((op) => op.grupo === grupo)
          .map((op) => op.operacao_id);
      } else {
        opsParaIniciar = [operacaoId];
      }

      // Alertar gestor se usar peças com anomalias
      if (loteObj && opInfo && qtdIniciar) {
        const qtdTotal = loteObj.quantidade_ajustada || loteObj.quantidade || 0;
        const qtdProd = opInfo?.quantidade_produzida_todos || 0;
        const totalAnomalias = loteObj.total_anomalias || 0;
        const qtdDisp = Math.max(0, qtdTotal - qtdProd - totalAnomalias);
        if (qtdIniciar > qtdDisp) {
          try {
            const opNome = operacoes.find((op) => op.id === operacaoId)?.nome || 'Operação';
            await axios.post(`${API}/alertas-anomalias`, {
              operador_id: user.id,
              operador_nome: user.nome || user.username,
              of_id: selectedOf.id,
              of_numero: selectedOf.numero_of,
              lote_id: lote,
              lote_codigo: loteObj.codigo_lote || loteObj.numero_lote || 'N/A',
              operacao_id: operacaoId,
              operacao_nome: opNome,
              quantidade_solicitada: qtdIniciar,
              quantidade_disponivel: qtdDisp,
              quantidade_anomalias: totalAnomalias,
            });
          } catch (alertErr) {
            console.error('[Alerta] Erro ao enviar alerta:', alertErr);
          }
        }
      }

      await axios.post(`${API}/producao/iniciar`, {
        operador_id: user.id,
        of_id: selectedOf.id,
        operacao_id: opsParaIniciar[0],
        operacoes_grupo: opsParaIniciar.length > 1 ? opsParaIniciar : null,
        lote_id: lote,
        quantidade_inicial: qtdIniciar,
      });

      toast.success('Vamos lá! Produção iniciada');
      await new Promise((resolve) => setTimeout(resolve, 300));
      if (isSimulation) {
        safeNavigate(`/sim/operador/${simUser}/producao`);
      } else {
        navigate('/operador/producao');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao iniciar'));
    } finally {
      setLoading && setLoading(false);
    }
  };

  // Handler de selecção de lote (contém toda a lógica de filtrar operações)
  const handleLoteChange = (val) => {
    setLote(val);
    setOperacaoId(null);
    setQtdIniciar(null);

    const loteObj = selectedOf?.lotes?.find((l) => l.id === val);
    let opsBase = opsOriginais;
    if (opsBase.length === 0) {
      const opsOF = selectedOf?.operacoes || [];
      opsBase = opsOF
        .map((opOf) => {
          const opInfo = operacoes.find((o) => o.id === opOf.operacao_id);
          return opInfo ? { ...opInfo, tempo_ajustado: opOf.tempo_ajustado } : null;
        })
        .filter(Boolean);
      setOpsOriginais(opsBase);
    }

    if (loteObj && opsBase.length > 0) {
      const opsDoLote = loteObj.operacoes_operador || [];
      const opsIdsDoLote = opsDoLote.map((o) => o.operacao_id);
      const categoriaIdLote = loteObj.categoria_id;

      let opsFiltradas = opsBase.filter((op) => {
        const estaNoLote = opsIdsDoLote.includes(op.id);
        const mesmCategoria = !categoriaIdLote || op.categoria_id === categoriaIdLote;
        return estaNoLote && mesmCategoria;
      });

      if (opsFiltradas.length === 0 && categoriaIdLote) {
        opsFiltradas = opsBase.filter((op) => op.categoria_id === categoriaIdLote);
      }

      const opsEnriquecidas = opsFiltradas.map((op) => {
        const opNoLote = opsDoLote.find((o) => o.operacao_id === op.id);
        const temCongelada = operacoesCongeladas.some(
          (c) => c.operacao_id === op.id && c.lote_id === loteObj.id
        );

        let statusOp = 'pendente';
        let desabilitada = false;

        if (temCongelada) {
          statusOp = 'congelada';
        } else if (opNoLote) {
          if (['completa', 'concluido', 'concluída'].includes(opNoLote.status)) {
            statusOp = 'terminada';
            desabilitada = true;
          } else {
            const qtdProd = opNoLote.quantidade_produzida_todos || 0;
            const qtdTotal = opNoLote.quantidade_total || loteObj.quantidade || 1;
            const disponivel = qtdTotal - qtdProd - (opNoLote.defeitos || 0) - (opNoLote.arranjos || 0);
            if (disponivel <= 0) { statusOp = 'terminada'; desabilitada = true; }
            else if (qtdProd > 0) statusOp = 'parcial';
          }
        }

        return {
          ...op, statusOperacao: statusOp, desabilitada,
          quantidade_produzida: opNoLote?.quantidade_produzida_todos || 0,
          quantidade_total: opNoLote?.quantidade_total || loteObj.quantidade || 0,
        };
      });

      const opsOrdenadas = opsEnriquecidas.sort((a, b) => {
        const ordem = { pendente: 0, parcial: 1, congelada: 2, terminada: 3 };
        return (ordem[a.statusOperacao] || 0) - (ordem[b.statusOperacao] || 0);
      });

      setOpsDisp(opsOrdenadas);
    } else {
      setOpsDisp([]);
    }
  };

  // Dados da operação/lote seleccionados
  const loteObj = selectedOf?.lotes?.find((l) => l.id === lote);
  const opSel = opsDisp.find((op) => op.id === operacaoId);
  const qtdTotal = loteObj ? (loteObj.quantidade_ajustada || loteObj.quantidade || 0) : 0;
  const qtdDisponivel = loteObj
    ? Math.max(0, qtdTotal - (loteObj.total_anomalias || 0) -
        (opsDisp.find((o) => o.id === operacaoId)?.quantidade_produzida || 0))
    : 0;
  const isContagem = opSel?.is_contagem || false;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm rounded-3xl max-h-[90vh] flex flex-col" data-testid="start-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-heading text-navy">Iniciar Tarefa</DialogTitle>
          <DialogDescription className="text-stone-500">
            Selecione o lote e a operação para começar
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
          {/* Aviso de produção ativa */}
          {producaoAtiva && (
            <div
              className="p-4 bg-red-50 border border-red-200 rounded-2xl cursor-pointer hover:bg-red-100 transition-all"
              onClick={() => {
                onOpenChange(false);
                if (isSimulation) safeNavigate(`/sim/operador/${simUser}/producao`);
                else navigate('/operador/producao');
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-red-800">Já tem produção ativa</p>
                  <p className="text-sm text-red-600">{producaoAtiva.of?.numero_of} • Clique para voltar</p>
                </div>
                <ChevronRight className="h-5 w-5 text-red-400" />
              </div>
            </div>
          )}

          {selectedOf && (
            <div className="p-4 bg-warm-subtle rounded-2xl">
              <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Ordem de Fabrico</p>
              <div className="flex items-center justify-between">
                <p className="font-heading font-bold text-xl text-navy">{selectedOf.numero_of}</p>
                <span className="text-sm font-medium text-primary bg-primary/10 px-2 py-1 rounded-lg">
                  {selectedOf.quantidade_total} un.
                </span>
              </div>
              <p className="text-sm text-stone-600">{selectedOf.cliente} • {selectedOf.artigo}</p>
            </div>
          )}

          {/* Seleção de Lote */}
          {selectedOf?.lotes && selectedOf.lotes.filter((l) => l.status === 'aprovado').length > 0 && (
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                1. Lote <span className="text-xs text-red-500">*</span>
                <span className="text-xs text-stone-500 ml-1">
                  ({selectedOf.lotes.filter((l) => l.status === 'aprovado').length} disponíveis)
                </span>
              </label>
              <Select value={lote} onValueChange={handleLoteChange}>
                <SelectTrigger className="h-14 rounded-xl border-2 border-primary/30 bg-primary/5" data-testid="lote-select">
                  <SelectValue placeholder="📦 Selecione o lote para começar" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {selectedOf.lotes.filter((l) => l.status === 'aprovado').map((lt) => {
                    const qtd = lt.quantidade_ajustada || lt.quantidade || 0;
                    const catId = lt.categoria_id;
                    const opsLote = (lt.operacoes_operador || []).filter(
                      (op) => !catId || op.categoria_id === catId
                    );
                    const opsPend = opsLote.filter((op) => {
                      if (['completa', 'concluido', 'concluída'].includes(op.status)) return false;
                      return (op.quantidade_produzida_todos || 0) < (op.quantidade_total || qtd || 1);
                    });
                    const isCompleto = opsPend.length === 0;

                    if (isCompleto) {
                      return (
                        <div key={lt.id} className="px-3 py-3 cursor-pointer hover:bg-blue-50 border-b border-stone-100 last:border-0"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); consultarLoteCompleto && consultarLoteCompleto(lt, selectedOf); }}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-navy">{lt.codigo_lote || `Lote ${lt.numero_lote}`}</span>
                              <span className="text-xs text-stone-500">{qtd} un.</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Completo</span>
                              <Eye className="h-4 w-4 text-blue-500" />
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <SelectItem key={lt.id} value={lt.id} className="py-3">
                        <div className="flex items-center justify-between w-full gap-4">
                          <div className="flex flex-col">
                            <span className="font-bold">{lt.codigo_lote || `Lote ${lt.numero_lote}`}</span>
                            <span className="text-xs text-stone-400">{qtd} un.</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-emerald-600">{opsPend.length} op{opsPend.length !== 1 ? 's' : ''} pend.</span>
                            {lt.urgente && <Zap className="h-3 w-3 text-amber-500" />}
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Seleção de Operação */}
          {lote && opsDisp.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                2. Operação <span className="text-xs text-red-500">*</span>
              </label>
              <Select value={operacaoId} onValueChange={(val) => { setOperacaoId(val); setQtdIniciar(null); }}>
                <SelectTrigger className="h-14 rounded-xl border-2 border-primary/30 bg-primary/5" data-testid="operacao-select">
                  <SelectValue placeholder="⚙️ Selecione a operação" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {opsDisp.map((op) => {
                    const isGrupo = op.isGrupo;
                    const statusColor = {
                      pendente: 'text-emerald-600',
                      parcial: 'text-amber-600',
                      congelada: 'text-blue-600',
                      terminada: 'text-stone-400',
                    }[op.statusOperacao] || 'text-stone-500';
                    const statusLabel = {
                      pendente: '✓ Pendente',
                      parcial: '⟳ Parcial',
                      congelada: '❄️ Congelada',
                      terminada: '✔ Terminada',
                    }[op.statusOperacao] || '';

                    return (
                      <SelectItem key={op.id} value={op.id} disabled={op.desabilitada} className="py-3">
                        <div className="flex items-center justify-between w-full gap-4">
                          <div className="flex flex-col">
                            <span className={`font-bold ${op.desabilitada ? 'text-stone-400' : ''}`}>
                              {isGrupo ? `🔗 Grupo ${op.grupo}` : `${op.codigo} - ${op.nome}`}
                            </span>
                            {op.tempo_ajustado > 0 && (
                              <span className="text-xs text-stone-400 flex items-center gap-1">
                                <Clock className="h-3 w-3" />{op.tempo_ajustado}s/peça
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className={`text-xs font-medium ${statusColor}`}>{statusLabel}</span>
                            {op.statusOperacao === 'congelada' && (
                              <Snowflake className="h-3 w-3 text-blue-500" />
                            )}
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Quantidade inicial (se não for contagem) */}
          {operacaoId && lote && !isContagem && (
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                3. Quantidade <span className="text-xs text-stone-500">(opcional)</span>
              </label>
              <Input
                type="number"
                min="1"
                max={qtdTotal}
                value={qtdIniciar || ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || null;
                  if (val && val > qtdTotal) setQtdIniciar(qtdTotal);
                  else setQtdIniciar(val);
                }}
                placeholder={`Máx: ${qtdTotal} peças`}
                className="h-12 rounded-xl border-2 border-stone-200"
                data-testid="quantidade-input"
              />
              {qtdDisponivel > 0 && qtdDisponivel < qtdTotal && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Disponível: {qtdDisponivel} peças (após anomalias)
                </p>
              )}
              {loteObj?.quantidade_ajustada && loteObj?.quantidade && loteObj.quantidade_ajustada !== loteObj.quantidade && (
                <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#92400E' }}>
                  <AlertTriangle className="h-3 w-3" />
                  Desvio de contagem: {loteObj.quantidade_ajustada - loteObj.quantidade} un. (Original: {loteObj.quantidade})
                </p>
              )}
            </div>
          )}

          {/* Resumo da tarefa */}
          {operacaoId && lote && loteObj && (
            <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
              <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Resumo da Tarefa</p>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-600">Lote</span>
                  <span className="font-bold text-navy">{loteObj?.codigo_lote || `Lote ${loteObj?.numero_lote}`}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-600">Operação</span>
                  <span className="font-medium text-stone-800 text-right max-w-[60%]">
                    {opSel ? `${opSel.codigo} - ${opSel.nome}` : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-600">Peças</span>
                  <span className="font-bold text-emerald-700">
                    {qtdIniciar ? `${qtdIniciar} de ${qtdTotal}` : `${qtdTotal} un.`}
                  </span>
                </div>
                {opSel?.statusOperacao === 'parcial' && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg mt-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      Operação já tem {opSel.quantidade_produzida} peças produzidas
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4 flex-shrink-0 border-t">
          <Button variant="outline" className="flex-1 h-12 rounded-xl" onClick={() => handleClose(false)}>
            Cancelar
          </Button>
          <Button
            className="flex-1 h-12 rounded-xl bg-primary hover:bg-primary/90"
            onClick={handleStart}
            disabled={loading || !lote || !operacaoId}
            data-testid="start-producao-btn"
          >
            <Play className="h-5 w-5 mr-2" />
            {loading ? 'A iniciar...' : 'Iniciar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
